


// sessionBreaks.js
const DPR_CAP = 1.25;
const DAY_SECONDS = 86400;
const SESSION_SECONDS = 21 * 3600;
const SESSION_FILL = "rgba(219,219,219,0.2)";

const utcDayStart = (ts) => Math.floor(Number(ts) / DAY_SECONDS) * DAY_SECONDS;
const utcDayOfWeek = (dayStart) => (Math.floor(dayStart / DAY_SECONDS) + 4) % 7;
const isWeekday = (dayStart) => {
  const dow = utcDayOfWeek(dayStart);
  return dow !== 0 && dow !== 6;
};

const lowerBound = (arr, value) => {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

const normalizeRangeTime = (time) => {
  if (typeof time === "number") return time;
  if (time && typeof time === "object" && "year" in time) {
    return Date.UTC(time.year, time.month - 1, time.day) / 1000;
  }
  const parsed = Date.parse(String(time));
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
};

export function initSessionBreaks({ container, chart, series, dataRef }) {
  if (!container || !chart || !series) return;

  const canvas = document.createElement("canvas");
  canvas.style.position = "absolute";
  canvas.style.left = "0";
  canvas.style.top = "0";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = 1;
  container.appendChild(canvas);

  const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true });
  if (!ctx) {
    canvas.remove();
    return;
  }

  const timeScale = chart.timeScale();

  let markTimes = [];
  let raf = null;
  let destroyed = false;
  let lastRenderKey = null;
  let dataVersion = 0;
  let lastCssW = 0;
  let lastCssH = 0;
  let lastDpr = 0;
  let lastDataRef = null;
  let lastDataLength = -1;
  let lastFirstTime = null;
  let lastLastTime = null;

  const ensureCanvasSize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    const w = Math.max(1, Math.round(container.clientWidth));
    const h = Math.max(1, Math.round(container.clientHeight));

    if (w !== lastCssW || h !== lastCssH || dpr !== lastDpr) {
      lastCssW = w;
      lastCssH = h;
      lastDpr = dpr;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { w, h, dpr };
  };

  const estimateCandleWidth = () => {
    const range = timeScale.getVisibleLogicalRange?.();
    if (!range) return 2;
    const bars = range.to - range.from;
    if (bars <= 0) return 2;
    return container.clientWidth / bars;
  };

  const buildSessionMarks = (candles) => {
    const len = candles.length;
    if (!len) {
      markTimes = [];
      return;
    }

    const result = [];
    let candleIndex = 0;
    let dayStart = utcDayStart(candles[0].time);
    const lastDayStart = utcDayStart(candles[len - 1].time);

    while (dayStart <= lastDayStart && candleIndex < len) {
      if (!isWeekday(dayStart)) {
        dayStart += DAY_SECONDS;
        continue;
      }

      const sessionTime = dayStart + SESSION_SECONDS;
      while (candleIndex < len && Number(candles[candleIndex].time) < sessionTime) {
        candleIndex++;
      }

      if (candleIndex < len) {
        result.push(Number(candles[candleIndex].time));
        candleIndex++;
      }

      dayStart += DAY_SECONDS;
    }

    markTimes = result;
  };

  const refreshSessionMarks = () => {
    const candles = Array.isArray(dataRef.current) ? dataRef.current : [];
    const len = candles.length;
    const firstTime = len ? candles[0].time : null;
    const lastTime = len ? candles[len - 1].time : null;

    if (
      candles === lastDataRef &&
      len === lastDataLength &&
      firstTime === lastFirstTime &&
      lastTime === lastLastTime
    ) {
      return false;
    }

    lastDataRef = candles;
    lastDataLength = len;
    lastFirstTime = firstTime;
    lastLastTime = lastTime;
    buildSessionMarks(candles);
    dataVersion++;
    lastRenderKey = null;
    return true;
  };

  const getVisibleMarkerBounds = () => {
    const visibleTimeRange = timeScale.getVisibleRange?.();
    const from = normalizeRangeTime(visibleTimeRange?.from);
    const to = normalizeRangeTime(visibleTimeRange?.to);
    if (from == null || to == null || from > to) {
      return { start: 0, end: markTimes.length };
    }

    return {
      start: Math.max(0, lowerBound(markTimes, from) - 1),
      end: Math.min(markTimes.length, lowerBound(markTimes, to) + 1),
    };
  };

  const draw = () => {
    if (destroyed) return;

    const visibleRange = timeScale.getVisibleLogicalRange?.();
    const { w, h, dpr } = ensureCanvasSize();
    const key = `${visibleRange?.from ?? ""}-${visibleRange?.to ?? ""}-${w}-${h}-${dpr}-${dataVersion}`;

    if (key === lastRenderKey) return;
    lastRenderKey = key;

    ctx.clearRect(0, 0, w, h);
    if (!markTimes.length) return;

    const candleWidth = Math.max(1, Math.floor(estimateCandleWidth() / 2));
    const { start, end } = getVisibleMarkerBounds();

    ctx.fillStyle = SESSION_FILL;
    for (let i = start; i < end; i++) {
      const x = timeScale.timeToCoordinate(markTimes[i]);
      if (!Number.isFinite(x)) continue;
      ctx.fillRect(x - candleWidth / 3, 0, candleWidth, h);
    }
  };

  const scheduleDraw = () => {
    if (destroyed || raf !== null) return;

    raf = requestAnimationFrame(() => {
      raf = null;
      refreshSessionMarks();
      draw();
    });
  };

  let resizeObserver = null;
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(scheduleDraw);
    resizeObserver.observe(container);
  }

  scheduleDraw();

  try { timeScale.subscribeVisibleLogicalRangeChange(scheduleDraw); } catch {}
  try { timeScale.subscribeVisibleTimeRangeChange(scheduleDraw); } catch {}
  window.addEventListener("resize", scheduleDraw);

  return {
    redraw: scheduleDraw,
    destroy: () => {
      destroyed = true;
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
      try { timeScale.unsubscribeVisibleLogicalRangeChange(scheduleDraw); } catch {}
      try { timeScale.unsubscribeVisibleTimeRangeChange(scheduleDraw); } catch {}
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleDraw);
      markTimes = [];
      canvas.width = 0;
      canvas.height = 0;
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    },
  };
}











// // weekly
// // sessionBreaks.js
// // Marks the first available candle of each MONTH (handles weekends/holidays).
// export function initSessionBreaks({ container, chart, series, dataRef }) {
//   if (!container || !chart || !series) return;

//   const canvas = document.createElement("canvas");
//   canvas.style.position = "absolute";
//   canvas.style.left = "0";
//   canvas.style.top = "0";
//   canvas.style.pointerEvents = "none";
//   canvas.style.zIndex = 1;
//   container.appendChild(canvas);

//   let ctx = canvas.getContext("2d");
//   let destroyed = false;
//   let rafId = null;

//   // marker cache + data-change detection
//   let monthMarkers = [];       // array of unix seconds
//   let lastDataRef = null;
//   let lastDataLen = -1;

//   // safe normalization: accept seconds or ms timestamps
//   function toUnixSeconds(ts) {
//     if (ts > 1e12) return Math.floor(ts / 1000); // ms -> s
//     return Math.floor(ts);
//   }

//   // month key: "YYYY-MM" in UTC
//   function monthKeyFromUnixSeconds(unixSec) {
//     const d = new Date(unixSec * 1000);
//     return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
//   }

//   // prepare monthMarkers when data changes (array identity OR length change)
//   function prepareMonthMarkers() {
//     const data = dataRef.current || [];
//     if (data === lastDataRef && data.length === lastDataLen) return;

//     lastDataRef = data;
//     lastDataLen = data.length;
//     const markers = [];
//     let prevKey = null;

//     for (let i = 0; i < data.length; i++) {
//       const item = data[i];
//       if (!item || typeof item.time === "undefined") continue;
//       const ts = toUnixSeconds(item.time);
//       const key = monthKeyFromUnixSeconds(ts);
//       if (key !== prevKey) {
//         markers.push(ts);
//         prevKey = key;
//       }
//     }

//     monthMarkers = markers;
//   }

//   // keep canvas sized to container only when dims/dpr change
//   let lastCssW = 0, lastCssH = 0, lastDpr = 0;
//   function ensureCanvasSize() {
//     if (!canvas || destroyed) return;
//     const dpr = window.devicePixelRatio || 1;
//     const cssW = container.clientWidth;
//     const cssH = container.clientHeight;
//     if (cssW === 0 || cssH === 0) return;

//     if (cssW !== lastCssW || cssH !== lastCssH || dpr !== lastDpr) {
//       lastCssW = cssW; lastCssH = cssH; lastDpr = dpr;
//       canvas.width = Math.round(cssW * dpr);
//       canvas.height = Math.round(cssH * dpr);
//       canvas.style.width = cssW + "px";
//       canvas.style.height = cssH + "px";
//       // reacquire context in case something removed it
//       ctx = canvas.getContext("2d");
//       if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
//     } else if (ctx) {
//       ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
//     }
//   }

//   function estimateCandleHalfWidth() {
//     const range = chart.timeScale().getVisibleLogicalRange();
//     if (!range) return 1;
//     const bars = range.to - range.from;
//     if (bars <= 0) return 1;
//     return Math.max(1, Math.floor((container.clientWidth / bars) / 2));
//   }

//   function draw() {
//     rafId = null;
//     if (destroyed || !canvas) return;

//     prepareMonthMarkers();
//     ensureCanvasSize();

//     const cssW = container.clientWidth;
//     const cssH = container.clientHeight;
//     if (cssW === 0 || cssH === 0) return;
//     if (!ctx) return;

//     ctx.clearRect(0, 0, cssW, cssH);

//     if (!monthMarkers || monthMarkers.length === 0) return;

//     const timeScale = chart.timeScale();
//     const halfWidth = estimateCandleHalfWidth();
//     ctx.fillStyle = "rgba(219,219,219,0.2)";

//     for (let i = 0; i < monthMarkers.length; i++) {
//       const x = timeScale.timeToCoordinate(monthMarkers[i]);
//       if (!isFinite(x)) continue;
//       ctx.fillRect(x - halfWidth / 3, 0, halfWidth, cssH);
//     }
//   }

//   function scheduleDraw() {
//     if (destroyed) return;
//     if (rafId !== null) return;
//     rafId = window.requestAnimationFrame(draw);
//   }

//   const ts = chart.timeScale();
//   const onVisibleLogicalRangeChange = () => scheduleDraw();
//   const onVisibleTimeRangeChange = () => scheduleDraw();

//   ts.subscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
//   ts.subscribeVisibleTimeRangeChange(onVisibleTimeRangeChange);

//   // ResizeObserver to detect container changes
//   const ro = new ResizeObserver(scheduleDraw);
//   ro.observe(container);

//   // fallback window resize
//   const onWindowResize = () => scheduleDraw();
//   window.addEventListener("resize", onWindowResize);

//   // initial draw
//   scheduleDraw();

//   return {
//     redraw: scheduleDraw,
//     destroy: () => {
//       destroyed = true;
//       if (rafId) cancelAnimationFrame(rafId);
//       ts.unsubscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
//       ts.unsubscribeVisibleTimeRangeChange(onVisibleTimeRangeChange);
//       ro.disconnect();
//       window.removeEventListener("resize", onWindowResize);
//       if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
//       ctx = null;
//       monthMarkers = [];
//       lastDataRef = null;
//       lastDataLen = -1;
//     },
//   };
// }
